import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Monitor, Smartphone, Cloud } from 'lucide-react';
import SectionTitle from './SectionTitle';

const ServicesSection = styled.section`
  padding: 120px 56px;
  background: ${({ theme }) => theme.colors.bg};
  position: relative;

  @media (max-width: 768px) {
    padding: 80px 20px;
  }
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => `rgba(${theme.colors.textRgb},0.10)`};
  border-radius: 12px;
  padding: 36px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: border-color 200ms ease;

  &:hover {
    border-color: rgba(255,91,58,0.4);
  }
`;

const IconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: rgba(255,91,58,0.12);
  border: 1px solid rgba(255,91,58,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FF5B3A;

  svg {
    width: 22px;
    height: 22px;
    stroke-width: 1.5;
  }
`;

const CardTitle = styled.h3`
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: 26px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const CardDesc = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  flex: 1;
`;

const services = [
  {
    icon: <Monitor />,
    title: 'Web Products',
    description: 'AI-native web products on Next.js and the edge. Streaming UI, real-time data, and interfaces designed around language models, not bolted onto them.',
  },
  {
    icon: <Smartphone />,
    title: 'Mobile Apps',
    description: 'Native and cross-platform mobile apps in Swift, Kotlin, and React Native. On-device inference, voice and camera-first flows, and the polish users actually feel.',
  },
  {
    icon: <Cloud />,
    title: 'AI Infrastructure',
    description: 'Inference, retrieval, and agent infrastructure on edge runtimes. Evals and observability built in, so the model is never the bottleneck.',
  },
];

const Services = () => (
  <ServicesSection id="services">
    <Container>
      <SectionTitle>What we build</SectionTitle>
      <ServicesGrid>
        {services.map((svc, i) => (
          <ServiceCard
            key={i}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <IconWrap>{svc.icon}</IconWrap>
            <CardTitle>{svc.title}</CardTitle>
            <CardDesc>{svc.description}</CardDesc>
          </ServiceCard>
        ))}
      </ServicesGrid>
    </Container>
  </ServicesSection>
);

export default Services;
